export const SECTION_NAME = "Shipment";

export const INTRO_TEXT = `In SOS Inventory, a shipment moves inventory from your company to the customer. The process does not necessarily mean that you actually shipped it. If, for example, a customer picks up the items from your location, a shipment transaction is still needed to indicate the items were removed from inventory.`;

export const EXAMPLE_JSON = `{
    "id": 123,
    "starred": 0,
    "syncToken": 0,
    "number": "SO-12345",
    "date": "2019-05-08T09:13:00",
    "customer": {
      "id": 23,
      "name": "Test Customer"
    },
    "location": {
      "id": 1,
      "name": "Warehouse"
    },
    "billing": {
      "company": "Test Company",
      "contact": null,
      "phone": "+18175551212",
      "email": "email@example.com",
      "addressName": "",
      "addressType": "",
      "address": {
        "line1": "123 Main",
        "line2": null,
        "line3": null,
        "line4": null,
        "line5": null,
        "city": "Syracuse",
        "stateProvince": "NY",
        "postalCode": "13202",
        "country": "US"
      }
    },
    "shipping": {
      "company": "Test Company",
      "contact": null,
      "phone": "+18175551212",
      "email": "email@example.com",
      "addressName": "",
      "addressType": "",
      "address": {
        "line1": "123 Main",
        "line2": null,
        "line3": null,
        "line4": null,
        "line5": null,
        "city": "Syracuse",
        "stateProvince": "NY",
        "postalCode": "13202",
        "country": "US"
      }
    },
    "channel": {
      "id": 3,
      "name": "Catalog"
    },
    "department": null,
    "priority": null,
    "assignedToUser": null,
    "shippingMethod": { 
      "id": 2,
      "name": "UPS"
    },
    "orderStage": null,
    "taxCode": null,
    "linkedTransaction": null,
    "customerMessage": null,
    "comment": null,
    "customerNotes": "",
    "customFields": [
      {
        "id": 5,
        "name": "Checkbox",
        "value": "false",
        "dataType": "Boolean"
      },
      {
        "id": 13,
        "name": "Cust-Acct-No",
        "value": null,
        "dataType": "Number"
      }
    ],
    "customerPO": null,
    "shipBy": "",
    "shippingAmount": 0,
    "total": 0,
    "forceToShipStation": false,
    "archived": false,
    "summaryOnly": false,
    "hasSignature": false,
    "trackingLink": "",
    "lines": [
      {
        "id": 123345,
        "lineNumber": 1,
        "item": {
          "id": 851,
          "name": "Widget"
        },
        "class": null,
        "job": null,
        "workcenter": null,
        "tax": null,
        "linkedTransaction": null,
        "description": null,
        "quantity": 1,
        "weight": 12,
        "volume": 12,
        "weightunit": "lb",
        "volumeunit": "cbm",
        "unitprice": 0,
        "amount": 0,
        "altAmount": 0,
        "picked": 0,
        "shipped": 0,
        "invoiced": 1,
        "produced": 0,
        "returned": 0,
        "cost": null,
        "margin": null,
        "listprice": 0,
        "percentdiscount": 0,
        "duedate": null,
        "uom": {
          "id": 2,
          "name": "doz"
        },
        "bin": null,
        "lot": null,
        "serials": null
      }
    ]
  }`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. ID field is ignored on create requests.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this transaction has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "number",
    type: "string",
    description: `The order number for this record.  If you wish to use the automatic numbering capability 
    on creation of a shipment, pass the string “auto”.`,
  },
  {
    name: "date",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Transaction date.`,
  },
  {
    name: "customer",
    type: "reference",
    description: "Customer for this transaction.",
  },
  {
    name: "location",
    type: "reference",
    description: `Location for this transaction.`,
  },
  {
    name: "billing",
    type: "transaction address",
    description: `Billing address.`,
  },
  {
    name: "shipping",
    type: "transaction address",
    description: `Shipping address.`,
  },
  {
    name: "channel",
    type: "reference",
    description: `Channel (e.g., Catalog, Retail Store) for this transaction.`,
  },
  {
    name: "department",
    type: "reference",
    description: `Department for this transaction.`,
  },
  {
    name: "priority",
    type: "reference",
    description: `The degree of importance or urgency assigned to this transaction.`,
  },
  {
    name: "assignedToUser",
    type: "reference",
    description: `User to whom this transaction is assigned.`,
  },
  {
    name: "shippingMethod",
    type: "reference",
    description: `Shipping method used for this transaction.`,
  },
  {
    name: "orderStage",
    type: "",
    description: "Unused.",
  },
  {
    name: "taxCode",
    type: "",
    description: "Unused.",
  },
  {
    name: "linkedTransaction",
    type: "",
    description: "Unused.",
  },
  {
    name: "customerMessage",
    type: "string",
    description: `Customer message field.`,
  },
  {
    name: "comment",
    type: "string",
    description:
      "The company’s internal comment about this transaction. This comment is not visible to the customer.",
  },
  {
    name: "customerNotes",
    type: "string",
    description: `Field for internal notes about customer.`,
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
  {
    name: "customerPO",
    type: "string",
    description: `Customer purchase order field.`,
  },
  {
    name: "shipBy",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Date by which this order should be shipped.`,
  },
  {
    name: "shippingAmount",
    type: "decimal",
    description: `Shipping amount field.`,
  },
  {
    name: "total",
    type: "",
    description: "Unused.",
  },
  {
    name: "forceToShipStation",
    type: "boolean",
    description: `A true response sends the transaction to ShipStation (shipping software) the next time it retrieves data from SOS, even if ShipStation does not request it.`,
  },
  {
    name: "archived",
    type: "boolean",
    description: `True if item is archived, false if not.`,
    readOnly: true,
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `Indicates if the summary parameter was set when retrieving back this record.`,
    readOnly: true,
  },
  {
    name: "hasSignature",
    type: "",
    description: `Reserved for future use.`,
    readOnly: true,
  },
  {
    name: "trackingLink",
    type: "string",
    description: `Link to a shipment tracking page for this transaction, if available.`,
    readOnly: true,
  },
  {
    name: "createBillForShippingAmount",
    type: "boolean",
    description: `A true response generates a bill to the customer for the shipping amount. Valid only on create and update.`,
  },
  {
    name: "lines",
    type: "array of shipment line items",
    description: `The lines for this shipment.  See object structure below.`,
  },
];

export const LINE_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this shipment line item. ID field is ignored on create requests.`,
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line on this shipment transaction.`,
  },
  {
    name: "item",
    type: "reference",
    description: `The item this line represents.`,
  },
  {
    name: "class",
    type: "reference",
    description: `The class for this line.`,
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`,
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`,
  },
  {
    name: "tax",
    type: "",
    description: "Unused.",
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this line.`,
  },
  {
    name: "description",
    type: "string",
    description: `The item description.`,
  },
  {
    name: "quantity",
    type: "decimal",
    description: `The quantity for this line item.`,
  },
  {
    name: "weight",
    type: "decimal",
    description: `The weight of this line.`,
    readOnly: true,
  },
  {
    name: "volume",
    type: "decimal",
    description: `The volume of this line.`,
    readOnly: true,
  },
  {
    name: "weightunit",
    type: "string",
    description: `The unit for the item's weight value.`,
    readOnly: true,
  },
  {
    name: "volumeunit",
    type: "string",
    description: `The unit for the item's volume value.`,
    readOnly: true,
  },
  {
    name: "unitPrice",
    type: "",
    description: "Unused.",
  },
  {
    name: "amount",
    type: "",
    description: "Unused.",
  },
  {
    name: "altAmount",
    type: "",
    description: "Unused.",
  },
  {
    name: "picked",
    type: "",
    description: "Unused.",
  },
  {
    name: "shipped",
    type: "",
    description: "Unused.",
  },
  {
    name: "invoiced",
    type: "decimal",
    description: `The number of items invoiced on this line.`,
  },
  {
    name: "produced",
    type: "",
    description: "Unused.",
  },
  {
    name: "returned",
    type: "",
    description: "Unused.",
  },
  {
    name: "cost",
    type: "",
    description: "Unused.",
  },
  {
    name: "margin",
    type: "",
    description: "Unused.",
  },
  {
    name: "listPrice",
    type: "",
    description: "Unused.",
  },
  {
    name: "percentDiscount",
    type: "",
    description: "Unused.",
  },
  {
    name: "dueDate",
    type: "",
    description: "Unused.",
  },
  {
    name: "uom",
    type: "reference",
    description: `The unit of measure for this line.`,
  },
  {
    name: "bin",
    type: "reference",
    description: `Bin where this item is located.`,
  },
  {
    name: "lot",
    type: "reference",
    description: `Lot to which this item belongs.`,
  },
  {
    name: "serials",
    type: "array of references",
    description: `Serial number references for this line item.`,
  },
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "summary",
    type: "",
    description: `If this parameter is present (the value doesn't matter, and doesn't need to be
     specified), only the summary attributes of the shipment will be returned.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, comment, customerPO, trackingNumber, customer name or location name.`,
  },
  {
    name: "archived",
    type: "string: yes/no",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`,
  },
  {
    name: "from/to",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Returns records based on the beginning and ending transaction dates specified. Both parameters are optional. Using only one parameter allows filtering in one direction. Example:
    from=2019-09-01T00:00:00&to=2019-09-10T00:00:00
   `,
  },
  {
    name: "location",
    type: "string",
    description: `Filters transactions according to the name of the location.`,
  },
  {
    name: "customerId",
    type: "number",
    description: `Filters transactions according to the customer id of the customer.`,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/shipment?start=101&maxresults=100&status=open&from=2019-09-01T00:00:00&to=2019-09-10T00:00:00&location=warehouse&customerId=237&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
